/* eslint camelcase: ["error", {allow: ["is_locked"]}] */
import { RoutalPalette, RoutalSelect } from '@nx-smartmonkey/ui';
import React, { useState } from 'react';

import { ContextualMenu } from '../../../../components/menus/ContextualMenu';
import { ConfirmDeleteDialog } from '../../../../dialogs/ConfirmDeleteDialog';
import { Driver } from '../../../../domain/driver/Driver';
import { Project } from '../../../../domain/project/Project';
import { Stop } from '../../../../domain/stop/Stop';
import { Task } from '../../../../domain/task/Task';
import { useRetrieveRightPanelInfo } from '../../../../hooks/right-panel/useRetrieveRightPanelInfo';
import Text from '../../../../react-components/Text';
import { useRetrieveActivePlanStops } from '../hooks/plan-details/useRetrieveActivePlanStops';
import { useDeleteTasks } from '../hooks/task/useDeleteTasks';

type PlanTasksActionsMenuType = `contextual` | `popover`;

type PlanTasksActionsMenuContextAnchor = {
  screenPosition: {
    top: number;
    left: number;
  };
};

type PlanTasksActionsMenuProps = {
  type?: PlanTasksActionsMenuType;
  planStops?: Stop[];
  planDrivers?: Driver[];
  activeProject?: Project;
  tasks: Task[];
  contextAnchor?: PlanTasksActionsMenuContextAnchor;
  setContextAnchor?: (context?: PlanTasksActionsMenuContextAnchor) => void;
  showViewDetails?: boolean;
  showDeleteDriver?: boolean;
  showUpdateDriverStatus?: boolean;
  showEndRoute?: boolean;
  popoverProps?: {
    hideArrow?: boolean;
    style?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    content?: React.ReactElement;
  };
};

export const PlanTasksActionsMenu = ({
  type = `contextual` as PlanTasksActionsMenuType,
  tasks,
  contextAnchor = {
    screenPosition: {
      top: 0,
      left: 0,
    },
  },
  setContextAnchor = () => {},
  showViewDetails = true,
  showDeleteDriver: showDeleteTask = true,
  showUpdateDriverStatus = true,
  showEndRoute = true,
  popoverProps,
}: PlanTasksActionsMenuProps) => {
  const [dialog, setDialog] = useState<`deleteTasks` | undefined>(undefined);
  const { run: deleteTasks } = useDeleteTasks();
  const { data: planStops } = useRetrieveActivePlanStops();
  const { setRightPanelInfo } = useRetrieveRightPanelInfo();

  if (tasks.length === 0) return null;

  return (
    <>
      {type === `popover` && (
        <RoutalSelect
          id="tasks-select-button-popover"
          hideArrow={popoverProps?.hideArrow}
          style={{
            background: `transparent`,
            width: `240px`,
            ...(popoverProps?.style ?? {}),
          }}
          containerStyle={popoverProps?.containerStyle}
          popoverProps={{
            maxHeight: `unset`,
            options: [
              ...(!showViewDetails || tasks.length !== 1
                ? []
                : [
                    {
                      key: `view_details`,
                      width: 240,
                      onClick: () => {
                        setRightPanelInfo({
                          detailType: `stop`,
                          detailId: `${tasks[0].stop_id}`,
                          detailTab: `tasks`,
                        });
                        // eventsPanel.driverActionsMenu(MixPanelEvents.driver.actions_menu.view_details);
                      },
                      content: <Text intlId="tasks.actions.view_details" style={{ cursor: `pointer` }} />,
                    },
                    null,
                  ]),
              ...(!showDeleteTask
                ? []
                : [
                    null,
                    {
                      key: `delete_task`,
                      width: 240,
                      onClick: () => {
                        setDialog(`deleteTasks`);
                      },
                      content: (
                        <Text
                          intlId="tasks.actions.delete_task"
                          style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                        />
                      ),
                    },
                  ]),
            ],
          }}
        >
          {popoverProps?.content}
        </RoutalSelect>
      )}

      {type === `contextual` && (
        <>
          {contextAnchor && (
            <ContextualMenu
              styledMenuItemStyles={{
                width: `240px`,
              }}
              anchorElementPosition={contextAnchor.screenPosition}
              onClose={() => {
                if (setContextAnchor) {
                  setContextAnchor(undefined);
                }
              }}
              options={[
                ...(showViewDetails && tasks.length === 1
                  ? [
                      {
                        key: `view_details`,
                        disabled: tasks.length !== 1,
                        text: <Text intlId="tasks.actions.view_details" style={{ cursor: `pointer` }} />,
                        onClick: (event: any) => {
                          event.stopPropagation();

                          setRightPanelInfo({
                            detailType: `stop`,
                            detailId: `${tasks[0].stop_id}`,
                            detailTab: `tasks`,
                          });
                          if (setContextAnchor) {
                            setContextAnchor(undefined);
                          }
                          // eventsPanel.taskActionsMenu(MixPanelEvents.task.actions_menu.view_details);
                        },
                      },
                      null,
                    ]
                  : []),
                ...(showDeleteTask
                  ? [
                      null,
                      {
                        key: `delete_task`,
                        text: (
                          <Text
                            intlId="tasks.actions.delete_task"
                            style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                          />
                        ),
                        onClick: (event: any) => {
                          event.stopPropagation();
                          setDialog(`deleteTasks`);
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </>
      )}

      <ConfirmDeleteDialog
        typeData="tasks"
        open={dialog === `deleteTasks`}
        onCloseDialog={() => {
          setDialog(undefined);
          if (setContextAnchor) {
            setContextAnchor(undefined);
          }
        }}
        data={tasks}
        handleDelete={() => {
          setDialog(undefined);

          if (setContextAnchor) {
            setContextAnchor(undefined);
          }

          const tasksToDelete = tasks;

          deleteTasks({ tasksIds: tasksToDelete.map((task) => task.id), stops: planStops ?? [] });

          // deleteDrivers({ taskIds: tasksToDelete.map((task) => task.id) });
          // eventsPanel.taskActionsMenu(MixPanelEvents.task.actions_menu.delete);
        }}
      />
    </>
  );
};
